import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Search,
  RefreshCw,
  MessageSquare,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Building2,
  Users,
  MoreHorizontal,
  ArrowUpRight,
  UserPlus,
  Plus,
  Mail,
  Phone,
  ExternalLink,
  Copy,
  Loader2,
  Briefcase,
  TrendingUp,
  Shield,
  XCircle,
  ChevronRight,
  FileText,
  X,
  Globe,
  MapPin,
  Edit3,
  Target,
  Sparkles,
  DollarSign,
  CalendarClock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stage configuration
const STAGE_CONFIG = {
  LEAD: {
    label: 'Lead',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    icon: MessageSquare,
    description: 'Contact form inquiry'
  },
  TRIAL_PENDING: {
    label: 'Pending',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Clock,
    description: 'Awaiting approval'
  },
  TRIAL_ACTIVE: {
    label: 'Trial',
    color: 'bg-teal-100 text-teal-700 border-teal-200',
    icon: CheckCircle2,
    description: '7-day trial active'
  },
  AT_RISK: {
    label: 'At Risk',
    color: 'bg-red-100 text-red-700 border-red-200',
    icon: AlertTriangle,
    description: 'Trial expiring soon'
  },
  ACTIVE: {
    label: 'Active',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    icon: Building2,
    description: 'Active contractor'
  },
  TRIAL_EXPIRED: {
    label: 'Expired',
    color: 'bg-slate-100 text-slate-600 border-slate-200',
    icon: XCircle,
    description: 'Trial ended'
  }
};

const SOURCE_CONFIG = {
  contact_form: { label: 'Contact Form', color: 'bg-blue-50 text-blue-600' },
  trial_request: { label: 'Trial Request', color: 'bg-teal-50 text-teal-600' },
  direct_create: { label: 'Direct', color: 'bg-slate-50 text-slate-600' }
};

export default function ContractorLifecycleDashboard() {
  const navigate = useNavigate();
  const { authToken } = useAuth();
  
  // Data state
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({
    total_leads: 0,
    pending_trials: 0,
    active_trials: 0,
    active_clients: 0,
    at_risk: 0,
    total: 0
  });
  const [consultants, setConsultants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  
  // Modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isAssignConsultantOpen, setIsAssignConsultantOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Promote form state
  const [promoteForm, setPromoteForm] = useState({
    company_name: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    contact_phone: '',
    business_size: '',
    requested_plan: 'GROWTH'
  });
  
  // Approve form state
  const [approveNotes, setApproveNotes] = useState('');
  const [approveResult, setApproveResult] = useState(null);
  
  // Assign consultant state
  const [selectedConsultant, setSelectedConsultant] = useState('');
  
  // Grant slots state
  const [isGrantSlotsOpen, setIsGrantSlotsOpen] = useState(false);
  const [slotsToGrant, setSlotsToGrant] = useState(1);
  const [grantDescription, setGrantDescription] = useState('');
  const [grantResult, setGrantResult] = useState(null);
  const [isExtendTrialOpen, setIsExtendTrialOpen] = useState(false);
  const [trialDaysToAdd, setTrialDaysToAdd] = useState(7);
  const [extendTrialResult, setExtendTrialResult] = useState(null);

  // Custom pricing state
  const [isCustomPriceOpen, setIsCustomPriceOpen] = useState(false);
  const [customPricePlans, setCustomPricePlans] = useState([]); // non-custom catalog plans
  const [customPricePlanId, setCustomPricePlanId] = useState('');
  const [customMonthlyFee, setCustomMonthlyFee] = useState('');
  const [customAnnualFee, setCustomAnnualFee] = useState('');
  const [customPriceNote, setCustomPriceNote] = useState('');
  const [isCustomPriceLoading, setIsCustomPriceLoading] = useState(false);
  const [isCustomPriceSaving, setIsCustomPriceSaving] = useState(false);

  // Quick View state
  const [quickViewItem, setQuickViewItem] = useState(null);
  const [organizationDetail, setOrganizationDetail] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  
  // Edit organization modal state
  const [isEditOrgModalOpen, setIsEditOrgModalOpen] = useState(false);
  const [editOrgForm, setEditOrgForm] = useState({});
  
  // Note modal state
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteText, setNoteText] = useState('');

  // Manual Add Trial Request modal state
  const [isManualTrialOpen, setIsManualTrialOpen] = useState(false);
  const [manualTrialResult, setManualTrialResult] = useState(null);
  const [manualTrialForm, setManualTrialForm] = useState({
    request_type: 'CONTRACTOR',
    company_name: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    contact_phone: '',
    contact_title: '',
    requested_plan: 'GROWTH',
    message: '',
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!authToken) return;
    setIsLoading(true);
    
    try {
      // Build query params
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (stageFilter !== 'all') params.append('stage', stageFilter);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      
      const [itemsRes, statsRes, consultantsRes] = await Promise.all([
        fetch(`${API_URL}/api/lifecycle/unified?${params}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }),
        fetch(`${API_URL}/api/lifecycle/stats`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }),
        fetch(`${API_URL}/api/lifecycle/consultants`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        })
      ]);
      
      if (itemsRes.ok) {
        const data = await itemsRes.json();
        setItems(data);
      }
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      
      if (consultantsRes.ok) {
        const data = await consultantsRes.json();
        setConsultants(data);
      }
    } catch (error) {
      console.error('Error fetching lifecycle data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [authToken, searchQuery, stageFilter, sourceFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch organization detail for Quick View
  const fetchOrganizationDetail = async (orgId) => {
    if (!orgId) return;
    setIsLoadingDetail(true);
    try {
      const response = await fetch(`${API_URL}/api/lifecycle/organizations/${orgId}/detail`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizationDetail(data);
      }
    } catch (error) {
      console.error('Error fetching organization detail:', error);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // Handle row click to open Quick View
  const handleRowClick = (item) => {
    setQuickViewItem(item);
    if (item.organization_id) {
      fetchOrganizationDetail(item.organization_id);
    } else {
      setOrganizationDetail(null);
    }
  };

  // Close Quick View
  const closeQuickView = () => {
    setQuickViewItem(null);
    setOrganizationDetail(null);
  };

  // Open edit organization modal
  const openEditOrgModal = () => {
    if (organizationDetail) {
      setEditOrgForm({
        uei: organizationDetail.uei || '',
        cage_code: organizationDetail.cage_code || '',
        website: organizationDetail.website || '',
        phone: organizationDetail.phone || '',
        industry_sector: organizationDetail.industry_sector || '',
        address_line1: organizationDetail.address?.line1 || '',
        address_city: organizationDetail.address?.city || '',
        address_state: organizationDetail.address?.state || '',
        address_zip: organizationDetail.address?.zip_code || '',
      });
      setIsEditOrgModalOpen(true);
    }
  };

  // Save organization edit
  const handleSaveOrgEdit = async () => {
    if (!quickViewItem?.organization_id) return;
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/lifecycle/organizations/${quickViewItem.organization_id}/detail`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          uei: editOrgForm.uei || null,
          cage_code: editOrgForm.cage_code || null,
          website: editOrgForm.website || null,
          phone: editOrgForm.phone || null,
          industry_sector: editOrgForm.industry_sector || null,
          address: editOrgForm.address_line1 ? {
            line1: editOrgForm.address_line1,
            city: editOrgForm.address_city,
            state: editOrgForm.address_state,
            zip_code: editOrgForm.address_zip,
            country: 'USA'
          } : null
        })
      });
      
      if (response.ok) {
        setIsEditOrgModalOpen(false);
        fetchOrganizationDetail(quickViewItem.organization_id);
      }
    } catch (error) {
      console.error('Error updating organization:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Open note modal
  const openOrgNoteModal = () => {
    setNoteText(organizationDetail?.internal_notes || '');
    setIsNoteModalOpen(true);
  };

  // Save organization note
  const handleSaveOrgNote = async () => {
    if (!quickViewItem?.organization_id) return;
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/lifecycle/organizations/${quickViewItem.organization_id}/detail`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ internal_notes: noteText })
      });
      
      if (response.ok) {
        setIsNoteModalOpen(false);
        fetchOrganizationDetail(quickViewItem.organization_id);
      }
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Promote lead to trial
  const handlePromote = async () => {
    if (!selectedItem) return;
    setIsProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/lifecycle/promote-to-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          lead_id: selectedItem.id,
          ...promoteForm
        })
      });
      
      if (response.ok) {
        setIsPromoteModalOpen(false);
        setSelectedItem(null);
        fetchData();
      } else {
        const err = await response.json();
        alert(`Error: ${err.detail}`);
      }
    } catch (error) {
      console.error('Error promoting lead:', error);
      alert('Failed to promote lead');
    } finally {
      setIsProcessing(false);
    }
  };

  // Approve trial request
  const handleApprove = async () => {
    if (!selectedItem) return;
    setIsProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/trial/requests/${selectedItem.id}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          admin_notes: approveNotes
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setApproveResult(data);
      } else {
        const err = await response.json();
        alert(`Error: ${err.detail}`);
      }
    } catch (error) {
      console.error('Error approving trial:', error);
      alert('Failed to approve trial');
    } finally {
      setIsProcessing(false);
    }
  };

  // Manual Add Trial Request handlers
  const handleSubmitManualTrial = async () => {
    if (
      !manualTrialForm.company_name ||
      !manualTrialForm.contact_first_name ||
      !manualTrialForm.contact_last_name ||
      !manualTrialForm.contact_email
    ) return;
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/trial/requests/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(manualTrialForm),
      });
      if (response.ok) {
        const data = await response.json();
        setManualTrialResult(data);
        fetchData();
        setTimeout(() => closeManualTrialModal(), 1500);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Failed: ${err.detail || response.statusText}`);
      }
    } catch (error) {
      console.error('Error creating manual trial request:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const closeManualTrialModal = () => {
    setIsManualTrialOpen(false);
    setTimeout(() => {
      setManualTrialResult(null);
      setManualTrialForm({
        request_type: 'CONTRACTOR',
        company_name: '',
        contact_first_name: '',
        contact_last_name: '',
        contact_email: '',
        contact_phone: '',
        contact_title: '',
        requested_plan: 'GROWTH',
        message: '',
      });
    }, 300);
  };

  // Toggle founding-member status for a contractor org. Skipped for
  // pending-trial rows that don't have an org yet.
  const toggleFoundingMember = async (orgId, currentValue) => {
    if (!orgId) return;
    try {
      const response = await fetch(
        `${API_URL}/api/billing/founding-member/toggle`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            organization_id: orgId,
            is_founding_member: !currentValue,
          }),
        }
      );
      if (response.ok) {
        fetchData();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Toggle failed: ${err.detail || response.statusText}`);
      }
    } catch (error) {
      console.error('Founding member toggle failed:', error);
    }
  };

  // Assign consultant
  const handleAssignConsultant = async () => {
    if (!selectedItem || !selectedConsultant) return;
    setIsProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/lifecycle/organizations/${selectedItem.organization_id || selectedItem.id}/assign-consultant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          consultant_org_id: selectedConsultant
        })
      });
      
      if (response.ok) {
        setIsAssignConsultantOpen(false);
        setSelectedItem(null);
        setSelectedConsultant('');
        fetchData();
      } else {
        const err = await response.json();
        alert(`Error: ${err.detail}`);
      }
    } catch (error) {
      console.error('Error assigning consultant:', error);
      alert('Failed to assign consultant');
    } finally {
      setIsProcessing(false);
    }
  };

  // Open promote modal with pre-filled data
  const openPromoteModal = (item) => {
    setSelectedItem(item);
    const nameParts = (item.contact_name || '').split(' ');
    setPromoteForm({
      company_name: item.company_name || '',
      contact_first_name: nameParts[0] || '',
      contact_last_name: nameParts.slice(1).join(' ') || '',
      contact_email: item.contact_email || '',
      contact_phone: item.contact_phone || '',
      business_size: item.business_size || '',
      requested_plan: 'GROWTH'
    });
    setIsPromoteModalOpen(true);
  };

  // Open approve modal
  const openApproveModal = (item) => {
    setSelectedItem(item);
    setApproveNotes('');
    setApproveResult(null);
    setIsApproveModalOpen(true);
  };

  // Open assign consultant modal
  const openAssignConsultantModal = (item) => {
    setSelectedItem(item);
    setSelectedConsultant(item.consultant_id || '');
    setIsAssignConsultantOpen(true);
  };

  // Open grant slots modal
  const openGrantSlotsModal = (item) => {
    setSelectedItem(item);
    setSlotsToGrant(1);
    setGrantDescription('');
    setGrantResult(null);
    setIsGrantSlotsOpen(true);
  };

  // Grant pursuit slots
  const handleGrantSlots = async () => {
    if (!selectedItem || !selectedItem.organization_id) return;
    setIsProcessing(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/trial/organizations/${selectedItem.organization_id}/grant-slots?slots=${slotsToGrant}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({ description: grantDescription })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setGrantResult(data);
        fetchData();
      } else {
        const err = await response.json();
        alert(`Error: ${err.detail}`);
      }
    } catch (error) {
      console.error('Error granting slots:', error);
      alert('Failed to grant slots');
    } finally {
      setIsProcessing(false);
    }
  };

  // Open extend trial modal
  const openExtendTrialModal = (item) => {
    setSelectedItem(item);
    setTrialDaysToAdd(7);
    setExtendTrialResult(null);
    setIsExtendTrialOpen(true);
  };

  // Extend an org's trial by an arbitrary number of days
  const handleExtendTrial = async () => {
    if (!selectedItem || !selectedItem.organization_id) return;
    setIsProcessing(true);

    try {
      const response = await fetch(
        `${API_URL}/api/trial/organizations/${selectedItem.organization_id}/extend`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({ days: trialDaysToAdd })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setExtendTrialResult(data);
        fetchData();
      } else {
        const err = await response.json();
        alert(`Error: ${err.detail}`);
      }
    } catch (error) {
      console.error('Error extending trial:', error);
      alert('Failed to extend trial');
    } finally {
      setIsProcessing(false);
    }
  };

  // Open the custom pricing modal: fetch the plan catalog (once) and the
  // org's current override so the form is prefilled.
  const openCustomPriceModal = async (item) => {
    if (!item.organization_id) return;
    setSelectedItem(item);
    setIsCustomPriceOpen(true);
    setIsCustomPriceLoading(true);
    try {
      if (customPricePlans.length === 0) {
        const plansRes = await fetch(`${API_URL}/api/billing/plans`);
        if (plansRes.ok) {
          const plansData = await plansRes.json();
          const all = [...(plansData.contractor_plans || []), ...(plansData.consultant_plans || [])]
            .filter((p) => !p.is_custom);
          setCustomPricePlans(all);
        }
      }
      const res = await fetch(
        `${API_URL}/api/lifecycle/organizations/${item.organization_id}/custom-pricing`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setCustomPricePlanId(data.subscription_plan || '');
        setCustomMonthlyFee(data.custom_monthly_fee != null ? String(data.custom_monthly_fee) : '');
        setCustomAnnualFee(data.custom_annual_fee != null ? String(data.custom_annual_fee) : '');
        setCustomPriceNote(data.custom_price_note || '');
      }
    } catch (error) {
      console.error('Error loading custom pricing:', error);
    } finally {
      setIsCustomPriceLoading(false);
    }
  };

  // Save (or clear, if both fee fields are blank) the custom pricing override
  const handleSaveCustomPrice = async () => {
    if (!selectedItem || !selectedItem.organization_id || !customPricePlanId) return;
    setIsCustomPriceSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/lifecycle/organizations/${selectedItem.organization_id}/custom-pricing`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            subscription_plan: customPricePlanId,
            custom_monthly_fee: customMonthlyFee.trim() ? parseFloat(customMonthlyFee) : null,
            custom_annual_fee: customAnnualFee.trim() ? parseFloat(customAnnualFee) : null,
            custom_price_note: customPriceNote.trim() || null,
          })
        }
      );
      if (response.ok) {
        setIsCustomPriceOpen(false);
        fetchData();
      } else {
        const err = await response.json();
        alert(`Error: ${err.detail}`);
      }
    } catch (error) {
      console.error('Error saving custom pricing:', error);
      alert('Failed to save custom pricing');
    } finally {
      setIsCustomPriceSaving(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Stats cards
  const statCards = [
    { label: 'Leads', value: stats.total_leads, icon: MessageSquare, color: 'text-purple-600', bgColor: 'bg-purple-50' },
    { label: 'Pending', value: stats.pending_trials, icon: Clock, color: 'text-amber-600', bgColor: 'bg-amber-50' },
    { label: 'In Trial', value: stats.active_trials, icon: CheckCircle2, color: 'text-teal-600', bgColor: 'bg-teal-50' },
    { label: 'Active', value: stats.active_clients, icon: Building2, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
    { label: 'At Risk', value: stats.at_risk, icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-50' },
  ];

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div data-testid="contractor-lifecycle-dashboard" className="flex">
      {/* Main Content */}
      <div className={cn("flex-1 transition-all duration-300", quickViewItem ? "mr-[420px]" : "")}>
      <PageHeader 
        title="Contractor Lifecycle"
        description="Unified view from lead to active contractor - manage the entire customer journey"
        action={
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setIsManualTrialOpen(true)}
              className="border-amber-200 text-amber-700 hover:bg-amber-50"
              data-testid="add-trial-request-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Trial Request
            </Button>
            <Button
              variant="outline"
              onClick={fetchData}
              disabled={isLoading}
              className="border-slate-200"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        }
      />
      
      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {statCards.map((stat, idx) => (
            <Card 
              key={idx} 
              className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => {
                if (stat.label === 'Leads') setStageFilter('LEAD');
                else if (stat.label === 'Pending') setStageFilter('TRIAL_PENDING');
                else if (stat.label === 'In Trial') setStageFilter('TRIAL_ACTIVE');
                else if (stat.label === 'Active') setStageFilter('ACTIVE');
                else if (stat.label === 'At Risk') setStageFilter('AT_RISK');
              }}
              data-testid={`stat-card-${stat.label.toLowerCase().replace(' ', '-')}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{stat.label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{stat.value}</p>
                  </div>
                  <div className={`p-2.5 ${stat.bgColor} rounded-lg`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, email, or company..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 border-slate-200"
                    data-testid="search-input"
                  />
                </div>
              </div>
              
              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger className="w-[160px] border-slate-200" data-testid="stage-filter">
                  <SelectValue placeholder="All Stages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  <SelectItem value="LEAD">Leads</SelectItem>
                  <SelectItem value="TRIAL_PENDING">Pending Trials</SelectItem>
                  <SelectItem value="TRIAL_ACTIVE">Active Trials</SelectItem>
                  <SelectItem value="AT_RISK">At Risk</SelectItem>
                  <SelectItem value="ACTIVE">Active Contractors</SelectItem>
                  <SelectItem value="TRIAL_EXPIRED">Expired</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-[160px] border-slate-200" data-testid="source-filter">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="contact_form">Contact Form</SelectItem>
                  <SelectItem value="trial_request">Trial Request</SelectItem>
                </SelectContent>
              </Select>
              
              {(stageFilter !== 'all' || sourceFilter !== 'all' || searchQuery) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setStageFilter('all');
                    setSourceFilter('all');
                    setSearchQuery('');
                  }}
                  className="text-slate-500"
                >
                  Clear filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Main Table */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-600 font-medium">No results found</p>
                <p className="text-slate-400 text-sm mt-1">Try adjusting your filters or search query</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="w-[280px]">Company / Contact</TableHead>
                    <TableHead className="w-[100px]">Source</TableHead>
                    <TableHead className="w-[100px]">Stage</TableHead>
                    <TableHead className="w-[120px]">Plan</TableHead>
                    <TableHead className="w-[150px]">Consultant</TableHead>
                    <TableHead className="w-[100px]">Capacity</TableHead>
                    <TableHead className="w-[80px]">Health</TableHead>
                    <TableHead className="w-[100px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => {
                    const stageConfig = STAGE_CONFIG[item.stage] || STAGE_CONFIG.LEAD;
                    const sourceConfig = SOURCE_CONFIG[item.source] || SOURCE_CONFIG.contact_form;
                    const StageIcon = stageConfig.icon;
                    
                    return (
                      <TableRow 
                        key={item.id} 
                        className={cn(
                          "hover:bg-slate-50 cursor-pointer transition-colors",
                          quickViewItem?.id === item.id && "bg-teal-50 hover:bg-teal-50"
                        )}
                        onClick={() => handleRowClick(item)}
                        data-testid={`lifecycle-row-${item.id}`}
                      >
                        {/* Company / Contact */}
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              item.stage === 'LEAD' ? 'bg-purple-50' : 
                              item.stage === 'TRIAL_PENDING' ? 'bg-amber-50' :
                              item.stage === 'AT_RISK' ? 'bg-red-50' :
                              'bg-teal-50'
                            }`}>
                              <StageIcon className={`w-5 h-5 ${
                                item.stage === 'LEAD' ? 'text-purple-600' : 
                                item.stage === 'TRIAL_PENDING' ? 'text-amber-600' :
                                item.stage === 'AT_RISK' ? 'text-red-600' :
                                'text-teal-600'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                {item.company_name || item.contact_name || 'Unknown'}
                                {item.is_founding_member && (
                                  <span
                                    className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-700 border border-amber-200 align-middle"
                                    title="Founding Member — 20% off for 12 months"
                                    data-testid={`founding-badge-${item.id}`}
                                  >
                                    <Sparkles className="w-2.5 h-2.5 mr-0.5" /> FM
                                  </span>
                                )}
                                {item.is_personal_email && (
                                  <span
                                    className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-700 align-middle"
                                    title="Submitted with a personal email — review before approving"
                                    data-testid={`personal-email-flag-${item.id}`}
                                  >
                                    PERSONAL EMAIL
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-slate-500">{item.contact_email}</p>
                            </div>
                          </div>
                        </TableCell>
                        
                        {/* Source */}
                        <TableCell>
                          <Badge variant="outline" className={`${sourceConfig.color} border-0 text-xs`}>
                            {sourceConfig.label}
                          </Badge>
                        </TableCell>
                        
                        {/* Stage */}
                        <TableCell>
                          <Badge className={`${stageConfig.color} border text-xs`}>
                            {stageConfig.label}
                            {item.trial_days_remaining !== null && item.trial_days_remaining !== undefined && item.stage !== 'TRIAL_EXPIRED' && (
                              <span className="ml-1">({item.trial_days_remaining}d)</span>
                            )}
                          </Badge>
                        </TableCell>
                        
                        {/* Plan */}
                        <TableCell>
                          {item.subscription_plan ? (
                            <Badge variant="secondary" className="text-xs">
                              {item.subscription_plan.replace('_', ' ')}
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </TableCell>
                        
                        {/* Consultant */}
                        <TableCell>
                          {item.consultant_name ? (
                            <div className="flex items-center gap-1.5">
                              <Briefcase className="w-3.5 h-3.5 text-slate-400" />
                              <span className="text-sm text-slate-600 truncate max-w-[120px]">
                                {item.consultant_name}
                              </span>
                            </div>
                          ) : item.can_assign_consultant ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs text-teal-600 hover:text-teal-700 p-0 h-auto"
                              onClick={() => openAssignConsultantModal(item)}
                            >
                              <UserPlus className="w-3.5 h-3.5 mr-1" />
                              Assign
                            </Button>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </TableCell>
                        
                        {/* Capacity */}
                        <TableCell>
                          {item.pursuit_capacity !== null && item.pursuit_capacity !== undefined ? (
                            <div className="text-sm">
                              <span className="font-medium text-slate-900">{item.active_pursuits || 0}</span>
                              <span className="text-slate-400">/{item.pursuit_capacity}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </TableCell>
                        
                        {/* Health */}
                        <TableCell>
                          {item.health_score !== null && item.health_score !== undefined ? (
                            <div className={`flex items-center gap-1 ${
                              item.health_score >= 70 ? 'text-emerald-600' :
                              item.health_score >= 40 ? 'text-amber-600' :
                              'text-red-600'
                            }`}>
                              <TrendingUp className="w-3.5 h-3.5" />
                              <span className="font-medium">{item.health_score}%</span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </TableCell>
                        
                        {/* Actions */}
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`actions-btn-${item.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {item.can_promote && (
                                <DropdownMenuItem onClick={() => openPromoteModal(item)}>
                                  <ArrowUpRight className="w-4 h-4 mr-2" />
                                  Promote to Trial
                                </DropdownMenuItem>
                              )}
                              
                              {item.can_approve && (
                                <DropdownMenuItem onClick={() => openApproveModal(item)}>
                                  <CheckCircle2 className="w-4 h-4 mr-2" />
                                  Approve Trial
                                </DropdownMenuItem>
                              )}
                              
                              {item.can_assign_consultant && (
                                <DropdownMenuItem onClick={() => openAssignConsultantModal(item)}>
                                  <UserPlus className="w-4 h-4 mr-2" />
                                  {item.consultant_name ? 'Change' : 'Assign'} Consultant
                                </DropdownMenuItem>
                              )}
                              
                              {item.organization_id && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => openGrantSlotsModal(item)}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Grant Slots
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => openCustomPriceModal(item)}
                                    data-testid={`set-custom-price-${item.id}`}
                                  >
                                    <DollarSign className="w-4 h-4 mr-2" />
                                    Set Custom Price
                                  </DropdownMenuItem>
                                  {(item.stage === 'TRIAL_ACTIVE' || item.stage === 'AT_RISK' || item.stage === 'TRIAL_EXPIRED') && (
                                    <DropdownMenuItem
                                      onClick={() => openExtendTrialModal(item)}
                                      data-testid={`extend-trial-${item.id}`}
                                    >
                                      <CalendarClock className="w-4 h-4 mr-2" />
                                      Extend Trial
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}

                              <DropdownMenuSeparator />
                              
                              <DropdownMenuItem onClick={() => copyToClipboard(item.contact_email)}>
                                <Mail className="w-4 h-4 mr-2" />
                                Copy Email
                              </DropdownMenuItem>
                              
                              {item.contact_phone && (
                                <DropdownMenuItem onClick={() => copyToClipboard(item.contact_phone)}>
                                  <Phone className="w-4 h-4 mr-2" />
                                  Copy Phone
                                </DropdownMenuItem>
                              )}

                              {/* Founding Member toggle — only for orgs that
                                  exist (skip pending-trial requests where
                                  organization_id is null). */}
                              {item.organization_id && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => toggleFoundingMember(item.organization_id, item.is_founding_member)}
                                    data-testid={`toggle-founding-${item.id}`}
                                  >
                                    <Sparkles className={item.is_founding_member ? 'w-4 h-4 mr-2 text-amber-500' : 'w-4 h-4 mr-2 text-slate-400'} />
                                    {item.is_founding_member ? 'Remove Founding Status' : 'Mark as Founding Member'}
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* PROMOTE TO TRIAL MODAL */}
      <Dialog open={isPromoteModalOpen} onOpenChange={setIsPromoteModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="promote-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5 text-teal-600" />
              Promote Lead to Trial
            </DialogTitle>
            <DialogDescription>
              Convert this contact inquiry into a trial request for approval.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input
                value={promoteForm.company_name}
                onChange={(e) => setPromoteForm({...promoteForm, company_name: e.target.value})}
                placeholder="Acme Corp"
                data-testid="promote-company-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={promoteForm.contact_first_name}
                  onChange={(e) => setPromoteForm({...promoteForm, contact_first_name: e.target.value})}
                  data-testid="promote-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={promoteForm.contact_last_name}
                  onChange={(e) => setPromoteForm({...promoteForm, contact_last_name: e.target.value})}
                  data-testid="promote-last-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                value={promoteForm.contact_email}
                onChange={(e) => setPromoteForm({...promoteForm, contact_email: e.target.value})}
                type="email"
                data-testid="promote-email"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={promoteForm.contact_phone}
                  onChange={(e) => setPromoteForm({...promoteForm, contact_phone: e.target.value})}
                  data-testid="promote-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Plan</Label>
                <Select 
                  value={promoteForm.requested_plan} 
                  onValueChange={(v) => setPromoteForm({...promoteForm, requested_plan: v})}
                >
                  <SelectTrigger data-testid="promote-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SMALL_BUSINESS">Small Business</SelectItem>
                    <SelectItem value="GROWTH">Growth</SelectItem>
                    <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {selectedItem?.message && (
              <div className="space-y-2">
                <Label className="text-slate-500">Original Message</Label>
                <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 max-h-24 overflow-y-auto">
                  {selectedItem.message}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPromoteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePromote}
              disabled={!promoteForm.company_name || !promoteForm.contact_email || isProcessing}
              className="bg-teal-700 hover:bg-teal-800 text-white"
              data-testid="promote-submit"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                  Create Trial Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* APPROVE TRIAL MODAL */}
      <Dialog open={isApproveModalOpen} onOpenChange={setIsApproveModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="approve-modal">
          {approveResult ? (
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Trial Approved!</h3>
              <p className="text-slate-600 mb-4">
                Organization <strong>{approveResult.organization_name}</strong> has been created.
              </p>
              
              <div className="bg-slate-50 rounded-lg p-4 text-left mb-6 space-y-3">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Invite URL</p>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-sm text-teal-700 break-all">{approveResult.invite_url}</code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(approveResult.invite_url)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {approveResult.temporary_password && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Temporary Password</p>
                    <div className="flex items-center gap-2 mt-1">
                      <code
                        className="text-sm text-amber-700 break-all bg-amber-50 px-2 py-1 rounded"
                        data-testid="approve-temp-password"
                      >
                        {approveResult.temporary_password}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(approveResult.temporary_password)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    {approveResult.welcome_email_sent === false && (
                      <p className="text-[11px] text-amber-600 mt-1">
                        Welcome email not sent yet — share this password with the customer manually until SMTP is configured.
                      </p>
                    )}
                  </div>
                )}
                {approveResult.admin_email && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Admin Login Email</p>
                    <p className="text-sm text-slate-700 mt-1 font-mono">{approveResult.admin_email}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-500 uppercase">Trial Expires</p>
                  <p className="text-sm text-slate-700 mt-1">
                    {new Date(approveResult.trial_expires_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              <Button 
                onClick={() => {
                  setIsApproveModalOpen(false);
                  setApproveResult(null);
                  fetchData();
                }} 
                className="bg-teal-700 hover:bg-teal-800 text-white"
              >
                Done
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  Approve Trial Request
                </DialogTitle>
                <DialogDescription>
                  Approve this trial request to create an organization and send an invite.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {selectedItem && (
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-500 text-sm">Company</span>
                      <span className="font-medium">{selectedItem.company_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500 text-sm">Contact</span>
                      <span className="font-medium">{selectedItem.contact_email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500 text-sm">Plan</span>
                      <Badge variant="secondary">{selectedItem.subscription_plan || 'GROWTH'}</Badge>
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Admin Notes (Optional)</Label>
                  <Input
                    value={approveNotes}
                    onChange={(e) => setApproveNotes(e.target.value)}
                    placeholder="Internal notes..."
                    data-testid="approve-notes"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsApproveModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleApprove}
                  disabled={isProcessing}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="approve-submit"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Approve & Create
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ASSIGN CONSULTANT MODAL */}
      <Dialog open={isAssignConsultantOpen} onOpenChange={setIsAssignConsultantOpen}>
        <DialogContent className="sm:max-w-md" data-testid="assign-consultant-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-teal-600" />
              Assign Consultant
            </DialogTitle>
            <DialogDescription>
              {selectedItem?.consultant_name 
                ? `Change consultant for ${selectedItem.company_name}`
                : `Assign a consultant to manage ${selectedItem?.company_name}`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="mb-2 block">Select Consultant</Label>
            <Select value={selectedConsultant} onValueChange={setSelectedConsultant}>
              <SelectTrigger data-testid="consultant-select">
                <SelectValue placeholder="Choose a consultant..." />
              </SelectTrigger>
              <SelectContent>
                {consultants.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {consultants.length === 0 && (
              <p className="text-sm text-slate-500 mt-2">
                No consultant organizations found. Create one first.
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAssignConsultantOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssignConsultant}
              disabled={!selectedConsultant || isProcessing}
              className="bg-teal-700 hover:bg-teal-800 text-white"
              data-testid="assign-consultant-submit"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign Consultant
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GRANT SLOTS MODAL */}
      <Dialog open={isGrantSlotsOpen} onOpenChange={setIsGrantSlotsOpen}>
        <DialogContent className="sm:max-w-md" data-testid="grant-slots-modal">
          {grantResult ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Slots Granted!</h3>
              <p className="text-slate-600 mb-4">
                Successfully granted {grantResult.slots_granted} slot(s) to {grantResult.organization_name}
              </p>
              <div className="bg-slate-50 rounded-lg p-4 text-left mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Previous</p>
                    <p className="font-bold">{grantResult.previous_purchased_slots}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">New Total</p>
                    <p className="font-bold text-teal-600">+{grantResult.new_purchased_slots}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Base Plan</p>
                    <p className="font-bold">{grantResult.base_plan_limit}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Total Capacity</p>
                    <p className="font-bold text-emerald-600">{grantResult.total_capacity}</p>
                  </div>
                </div>
              </div>
              <Button onClick={() => setIsGrantSlotsOpen(false)} className="bg-teal-700 hover:bg-teal-800 text-white">
                Done
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5 text-teal-600" />
                  Grant Pursuit Slots
                </DialogTitle>
                <DialogDescription>
                  Add pursuit capacity to {selectedItem?.company_name}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="font-medium">{selectedItem?.company_name}</p>
                  <p className="text-sm text-slate-500">
                    Current: {selectedItem?.pursuit_capacity - (selectedItem?.purchased_slots || 0)} base + {selectedItem?.purchased_slots || 0} purchased = {selectedItem?.pursuit_capacity || 0} total
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Number of Slots to Grant</Label>
                  <Select value={String(slotsToGrant)} onValueChange={(v) => setSlotsToGrant(Number(v))}>
                    <SelectTrigger data-testid="slots-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n} slot{n > 1 ? 's' : ''} (${(n * 150).toFixed(2)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Description (Optional)</Label>
                  <Input
                    placeholder="e.g., Payment received via check #1234"
                    value={grantDescription}
                    onChange={(e) => setGrantDescription(e.target.value)}
                    data-testid="grant-description"
                  />
                </div>
                
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-teal-700">Amount to Record:</span>
                    <span className="text-lg font-bold text-teal-800">
                      ${(slotsToGrant * 150).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsGrantSlotsOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={handleGrantSlots}
                  disabled={isProcessing}
                  data-testid="confirm-grant-btn"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Granting...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Grant {slotsToGrant} Slot{slotsToGrant > 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* EXTEND TRIAL MODAL */}
      <Dialog open={isExtendTrialOpen} onOpenChange={setIsExtendTrialOpen}>
        <DialogContent className="sm:max-w-md" data-testid="extend-trial-modal">
          {extendTrialResult ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Trial Extended!</h3>
              <p className="text-slate-600 mb-4">
                Extended by {extendTrialResult.extended_by_days} day{extendTrialResult.extended_by_days > 1 ? 's' : ''}.
                New expiration: {new Date(extendTrialResult.trial_expires_at).toLocaleDateString()}
              </p>
              <Button onClick={() => setIsExtendTrialOpen(false)} className="bg-teal-700 hover:bg-teal-800 text-white">
                Done
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CalendarClock className="w-5 h-5 text-teal-600" />
                  Extend Trial
                </DialogTitle>
                <DialogDescription>
                  Add trial days for {selectedItem?.company_name}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="font-medium">{selectedItem?.company_name}</p>
                  <p className="text-sm text-slate-500">
                    {selectedItem?.stage === 'TRIAL_EXPIRED'
                      ? 'Trial has expired'
                      : selectedItem?.trial_expires_at
                        ? `Currently expires ${new Date(selectedItem.trial_expires_at).toLocaleDateString()}`
                        : 'No current expiration on file'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Days to Add</Label>
                  <Input
                    type="number"
                    min={1}
                    max={3650}
                    value={trialDaysToAdd}
                    onChange={(e) => setTrialDaysToAdd(Math.max(1, parseInt(e.target.value) || 1))}
                    data-testid="trial-days-input"
                  />
                  <p className="text-xs text-slate-400">
                    Extends from the current expiration date, or from today if the trial has already expired.
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsExtendTrialOpen(false)}>
                  Cancel
                </Button>
                <Button
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={handleExtendTrial}
                  disabled={isProcessing}
                  data-testid="confirm-extend-trial-btn"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Extending...
                    </>
                  ) : (
                    <>
                      <CalendarClock className="w-4 h-4 mr-2" />
                      Extend by {trialDaysToAdd} Day{trialDaysToAdd > 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* CUSTOM PRICING MODAL */}
      <Dialog open={isCustomPriceOpen} onOpenChange={setIsCustomPriceOpen}>
        <DialogContent className="sm:max-w-md" data-testid="custom-price-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-teal-600" />
              Set Custom Price
            </DialogTitle>
            <DialogDescription>
              Negotiated billing override for {selectedItem?.company_name}. Feature access
              still follows the selected plan — only the charged amount changes.
            </DialogDescription>
          </DialogHeader>

          {isCustomPriceLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>Base Plan (drives feature access)</Label>
                  <Select value={customPricePlanId} onValueChange={setCustomPricePlanId}>
                    <SelectTrigger data-testid="custom-price-plan-select">
                      <SelectValue placeholder="Select a plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {customPricePlans.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} ({p.org_type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Monthly Fee ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Catalog price"
                      value={customMonthlyFee}
                      onChange={(e) => setCustomMonthlyFee(e.target.value)}
                      data-testid="custom-monthly-fee-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Annual Fee ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Catalog price"
                      value={customAnnualFee}
                      onChange={(e) => setCustomAnnualFee(e.target.value)}
                      data-testid="custom-annual-fee-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Leave a fee blank to use the plan's catalog price for that cycle. Leave both
                  blank and save to clear the override entirely.
                </p>

                <div className="space-y-2">
                  <Label>Note (Optional)</Label>
                  <Textarea
                    placeholder="e.g., Negotiated 2026 renewal rate"
                    value={customPriceNote}
                    onChange={(e) => setCustomPriceNote(e.target.value)}
                    data-testid="custom-price-note"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCustomPriceOpen(false)}>
                  Cancel
                </Button>
                <Button
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={handleSaveCustomPrice}
                  disabled={isCustomPriceSaving || !customPricePlanId}
                  data-testid="confirm-custom-price-btn"
                >
                  {isCustomPriceSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <DollarSign className="w-4 h-4 mr-2" />
                      Save
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* QUICK VIEW PANEL */}
      {quickViewItem && (
        <div className="fixed right-0 top-0 h-full w-[420px] bg-slate-50 border-l border-slate-200 shadow-xl z-40 overflow-y-auto" data-testid="contractor-quick-view">
          {/* Panel Header */}
          <div className={cn(
            "px-6 py-4 text-white",
            quickViewItem.stage === 'LEAD' ? 'bg-purple-700' :
            quickViewItem.stage === 'TRIAL_PENDING' ? 'bg-amber-600' :
            quickViewItem.stage === 'AT_RISK' ? 'bg-red-600' :
            quickViewItem.stage === 'ACTIVE' ? 'bg-emerald-700' :
            'bg-teal-700'
          )}>
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-semibold uppercase tracking-wider opacity-80">
                {quickViewItem.stage === 'LEAD' ? 'Lead' : 
                 quickViewItem.stage === 'TRIAL_PENDING' ? 'Trial Request' :
                 quickViewItem.stage === 'TRIAL_ACTIVE' ? 'Trial Account' :
                 'Contractor'} Details
              </p>
              <button 
                onClick={closeQuickView}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">{quickViewItem.company_name || quickViewItem.contact_name || 'Unknown'}</h3>
                <p className="text-sm opacity-80">{quickViewItem.contact_email}</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Stage Badge */}
            <div className="flex items-center justify-between">
              <Badge className={cn("border", STAGE_CONFIG[quickViewItem.stage]?.color)}>
                {STAGE_CONFIG[quickViewItem.stage]?.label || quickViewItem.stage}
                {quickViewItem.trial_days_remaining !== null && quickViewItem.trial_days_remaining !== undefined && (
                  <span className="ml-1">({quickViewItem.trial_days_remaining}d left)</span>
                )}
              </Badge>
              {quickViewItem.subscription_plan && (
                <Badge variant="secondary">{quickViewItem.subscription_plan.replace('_', ' ')}</Badge>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Contact Information</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-700">{quickViewItem.contact_email}</span>
                </div>
                {quickViewItem.contact_phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700">{quickViewItem.contact_phone}</span>
                  </div>
                )}
                {quickViewItem.contact_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700">{quickViewItem.contact_name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Organization Details - Only if has organization_id */}
            {quickViewItem.organization_id && (
              <>
                {isLoadingDetail ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                  </div>
                ) : organizationDetail ? (
                  <>
                    {/* Business Identity */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Business Identity</h4>
                      <div className="space-y-2">
                        {organizationDetail.uei && (
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                            <span className="text-sm text-slate-500">UEI</span>
                            <span className="font-mono font-medium text-slate-900">{organizationDetail.uei}</span>
                          </div>
                        )}
                        {organizationDetail.cage_code && (
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                            <span className="text-sm text-slate-500">CAGE Code</span>
                            <span className="font-mono font-medium text-slate-900">{organizationDetail.cage_code}</span>
                          </div>
                        )}
                        {organizationDetail.website && (
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                            <span className="text-sm text-slate-500">Website</span>
                            <a href={organizationDetail.website.startsWith('http') ? organizationDetail.website : `https://${organizationDetail.website}`}
                               target="_blank" rel="noopener noreferrer"
                               className="text-teal-600 hover:underline text-sm">
                              {organizationDetail.website}
                            </a>
                          </div>
                        )}
                        {organizationDetail.industry_sector && (
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                            <span className="text-sm text-slate-500">Industry</span>
                            <Badge variant="secondary">{organizationDetail.industry_sector}</Badge>
                          </div>
                        )}
                        {!organizationDetail.uei && !organizationDetail.cage_code && !organizationDetail.website && (
                          <p className="text-sm text-slate-400 italic">No business identity info yet</p>
                        )}
                      </div>
                    </div>

                    {/* Address */}
                    {organizationDetail.address?.line1 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">Address</h4>
                        <div className="flex items-start gap-2 text-sm text-slate-600">
                          <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                          <div>
                            <p>{organizationDetail.address.line1}</p>
                            {organizationDetail.address.city && (
                              <p>{organizationDetail.address.city}, {organizationDetail.address.state} {organizationDetail.address.zip_code}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Primary Admin */}
                    {organizationDetail.primary_admin && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">Primary Admin</h4>
                        <div className="p-3 bg-white rounded-lg border border-slate-200">
                          <p className="font-medium text-slate-900">{organizationDetail.primary_admin.name}</p>
                          <p className="text-sm text-slate-500">{organizationDetail.primary_admin.email}</p>
                          {organizationDetail.primary_admin.phone && (
                            <p className="text-sm text-slate-500">{organizationDetail.primary_admin.phone}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Capacity Stats */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Capacity</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-white rounded-lg border border-slate-200 text-center">
                          <p className="text-2xl font-bold text-teal-700">{organizationDetail.active_pursuits || 0}</p>
                          <p className="text-xs text-slate-500">Active Pursuits</p>
                        </div>
                        <div className="p-3 bg-white rounded-lg border border-slate-200 text-center">
                          <p className="text-2xl font-bold text-blue-700">{organizationDetail.max_pursuits || 0}</p>
                          <p className="text-xs text-slate-500">Max Capacity</p>
                        </div>
                      </div>
                    </div>

                    {/* Consultant */}
                    {organizationDetail.consultant_name && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">Assigned Consultant</h4>
                        <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200">
                          <Briefcase className="w-4 h-4 text-teal-600" />
                          <span className="text-sm text-slate-700">{organizationDetail.consultant_name}</span>
                        </div>
                      </div>
                    )}

                    {/* Internal Notes */}
                    {organizationDetail.internal_notes && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">Internal Notes</h4>
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-slate-700">
                          {organizationDetail.internal_notes}
                        </div>
                      </div>
                    )}

                    {/* Timestamps */}
                    <div className="text-xs text-slate-400">
                      <p>Created: {formatDate(organizationDetail.created_at)}</p>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3 pt-4 border-t border-slate-200">
                      <Button 
                        className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                        onClick={openEditOrgModal}
                      >
                        <Edit3 className="w-4 h-4 mr-2" />
                        Edit Profile
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full border-slate-200"
                        onClick={openOrgNoteModal}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        {organizationDetail.internal_notes ? 'Edit Note' : 'Add Note'}
                      </Button>
                    </div>
                  </>
                ) : null}
              </>
            )}

            {/* Lead-only section (no organization yet) */}
            {!quickViewItem.organization_id && (
              <>
                {quickViewItem.message && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Original Message</h4>
                    <div className="p-3 bg-white rounded-lg border border-slate-200 text-sm text-slate-600 max-h-32 overflow-y-auto">
                      {quickViewItem.message}
                    </div>
                  </div>
                )}

                <div className="text-xs text-slate-400">
                  <p>Submitted: {formatDate(quickViewItem.created_at)}</p>
                </div>

                {/* Actions for leads */}
                {quickViewItem.can_promote && (
                  <div className="pt-4 border-t border-slate-200">
                    <Button 
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={() => openPromoteModal(quickViewItem)}
                    >
                      <ArrowUpRight className="w-4 h-4 mr-2" />
                      Promote to Trial
                    </Button>
                  </div>
                )}

                {quickViewItem.can_approve && (
                  <div className="pt-4 border-t border-slate-200">
                    <Button 
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => openApproveModal(quickViewItem)}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Approve Trial
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* EDIT ORGANIZATION MODAL */}
      <Dialog open={isEditOrgModalOpen} onOpenChange={setIsEditOrgModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-org-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-teal-600" />
              Edit Organization Profile
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Business Identity */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>UEI</Label>
                <Input
                  value={editOrgForm.uei || ''}
                  onChange={(e) => setEditOrgForm({...editOrgForm, uei: e.target.value})}
                  placeholder="H93L94K2M1P5"
                  maxLength={12}
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>CAGE Code</Label>
                <Input
                  value={editOrgForm.cage_code || ''}
                  onChange={(e) => setEditOrgForm({...editOrgForm, cage_code: e.target.value})}
                  placeholder="7Z9X2"
                  maxLength={5}
                  className="font-mono"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={editOrgForm.website || ''}
                  onChange={(e) => setEditOrgForm({...editOrgForm, website: e.target.value})}
                  placeholder="www.company.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={editOrgForm.phone || ''}
                  onChange={(e) => setEditOrgForm({...editOrgForm, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Industry Sector</Label>
              <Input
                value={editOrgForm.industry_sector || ''}
                onChange={(e) => setEditOrgForm({...editOrgForm, industry_sector: e.target.value})}
                placeholder="IT Services, Defense"
              />
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={editOrgForm.address_line1 || ''}
                onChange={(e) => setEditOrgForm({...editOrgForm, address_line1: e.target.value})}
                placeholder="123 Main Street"
                className="mb-2"
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  value={editOrgForm.address_city || ''}
                  onChange={(e) => setEditOrgForm({...editOrgForm, address_city: e.target.value})}
                  placeholder="City"
                />
                <Input
                  value={editOrgForm.address_state || ''}
                  onChange={(e) => setEditOrgForm({...editOrgForm, address_state: e.target.value})}
                  placeholder="State"
                  maxLength={2}
                />
                <Input
                  value={editOrgForm.address_zip || ''}
                  onChange={(e) => setEditOrgForm({...editOrgForm, address_zip: e.target.value})}
                  placeholder="ZIP"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOrgModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveOrgEdit}
              disabled={isProcessing}
              className="bg-teal-700 hover:bg-teal-800 text-white"
            >
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ORGANIZATION NOTE MODAL */}
      <Dialog open={isNoteModalOpen} onOpenChange={setIsNoteModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="org-note-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              Internal Notes
            </DialogTitle>
            <DialogDescription>
              Add private notes about this organization (only visible to Super Admin)
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add your notes here... (Markdown supported)"
              rows={6}
              className="resize-none"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNoteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveOrgNote}
              disabled={isProcessing}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MANUAL ADD TRIAL REQUEST MODAL */}
      <Dialog open={isManualTrialOpen} onOpenChange={(open) => { if (!open) closeManualTrialModal(); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="manual-trial-modal">
          {manualTrialResult ? (
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Trial Request Created</h3>
              <p className="text-slate-600 mb-4">
                A pending trial request was added. It will appear with status "Pending" — use the row's Approve action to provision the workspace.
              </p>
              <Button onClick={closeManualTrialModal} className="bg-teal-700 hover:bg-teal-800 text-white">
                Done
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-amber-600" />
                  Add Trial Request (Manual)
                </DialogTitle>
                <DialogDescription>
                  Manually create a pending trial request for someone who couldn't sign up themselves.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Request Type *</Label>
                  <Select
                    value={manualTrialForm.request_type}
                    onValueChange={(v) => setManualTrialForm({ ...manualTrialForm, request_type: v })}
                  >
                    <SelectTrigger data-testid="manual-trial-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CONTRACTOR">Contractor</SelectItem>
                      <SelectItem value="CONSULTANCY">Consultancy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={manualTrialForm.company_name}
                    onChange={(e) => setManualTrialForm({ ...manualTrialForm, company_name: e.target.value })}
                    placeholder="Acme Federal Inc."
                    data-testid="manual-trial-company"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input
                      value={manualTrialForm.contact_first_name}
                      onChange={(e) => setManualTrialForm({ ...manualTrialForm, contact_first_name: e.target.value })}
                      data-testid="manual-trial-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input
                      value={manualTrialForm.contact_last_name}
                      onChange={(e) => setManualTrialForm({ ...manualTrialForm, contact_last_name: e.target.value })}
                      data-testid="manual-trial-last-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={manualTrialForm.contact_email}
                    onChange={(e) => setManualTrialForm({ ...manualTrialForm, contact_email: e.target.value })}
                    placeholder="contact@company.com"
                    data-testid="manual-trial-email"
                  />
                  {manualTrialForm.contact_email &&
                   ['gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com','icloud.com'].includes(
                     (manualTrialForm.contact_email.split('@')[1] || '').toLowerCase()
                   ) && (
                    <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <span>Personal email domain — will be flagged but accepted.</span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={manualTrialForm.contact_phone}
                      onChange={(e) => setManualTrialForm({ ...manualTrialForm, contact_phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={manualTrialForm.contact_title}
                      onChange={(e) => setManualTrialForm({ ...manualTrialForm, contact_title: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Plan</Label>
                  <Select
                    value={manualTrialForm.requested_plan}
                    onValueChange={(v) => setManualTrialForm({ ...manualTrialForm, requested_plan: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="STARTER">Starter</SelectItem>
                      <SelectItem value="GROWTH">Growth</SelectItem>
                      <SelectItem value="SCALE">Scale</SelectItem>
                      <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Internal Note</Label>
                  <Textarea
                    value={manualTrialForm.message}
                    onChange={(e) => setManualTrialForm({ ...manualTrialForm, message: e.target.value })}
                    placeholder="Why this is being added manually..."
                    rows={2}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={closeManualTrialModal}>Cancel</Button>
                <Button
                  onClick={handleSubmitManualTrial}
                  disabled={
                    !manualTrialForm.company_name ||
                    !manualTrialForm.contact_first_name ||
                    !manualTrialForm.contact_last_name ||
                    !manualTrialForm.contact_email ||
                    isProcessing
                  }
                  className="bg-teal-700 hover:bg-teal-800 text-white"
                  data-testid="submit-manual-trial-btn"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create Trial Request
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
